import { useEffect, useState, useCallback } from 'react'

/**
 * SectionDebugOverlay — toggle: show section name + number every content element.
 * Helps label sections/elements for design communication.
 */
export default function SectionDebugOverlay() {
  const [on, setOn] = useState(false)

  const annotate = useCallback(() => {
    // Clear previous
    document.querySelectorAll('[data-debug-badge]').forEach((el) => el.remove())
    document.querySelectorAll('[data-debug-num]').forEach((el) => {
      el.removeAttribute('data-debug-num')
      if (el.style) {
        el.style.outline = ''
        el.style.outlineOffset = ''
      }
    })
    document.querySelectorAll('[data-debug-section-wrap]').forEach((el) => {
      el.style.outline = ''
      el.style.outlineOffset = ''
      el.removeAttribute('data-debug-section-wrap')
    })

    if (!on) return

    const sections = document.querySelectorAll('main section, main [data-section]')
    sections.forEach((section) => {
      const name =
        section.getAttribute('data-section') ||
        section.getAttribute('data-testid') ||
        section.id ||
        section.tagName

      section.setAttribute('data-debug-section-wrap', '1')
      section.style.outline = '2px solid rgba(34, 65, 96, 0.55)'
      section.style.outlineOffset = '-2px'
      section.style.position = section.style.position || 'relative'

      const badge = document.createElement('div')
      badge.setAttribute('data-debug-badge', '1')
      badge.textContent = name
      Object.assign(badge.style, {
        position: 'absolute',
        top: '8px',
        left: '8px',
        zIndex: '9998',
        background: 'rgba(34, 65, 96, 0.92)',
        color: '#EDE7DC',
        fontFamily: 'DM Sans, system-ui, sans-serif',
        fontSize: '11px',
        fontWeight: '600',
        letterSpacing: '0.04em',
        padding: '6px 10px',
        borderRadius: '4px',
        pointerEvents: 'none',
        maxWidth: '90%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      })
      // ensure positioning context
      const cs = window.getComputedStyle(section)
      if (cs.position === 'static') section.style.position = 'relative'
      section.prepend(badge)

      // Number every meaningful content node
      const candidates = section.querySelectorAll(
        'h1, h2, h3, h4, h5, h6, p, li, a, button, img, label, span.typography-label, [data-testid], blockquote, figcaption, input, textarea, svg'
      )
      let n = 0
      candidates.forEach((el) => {
        if (el.closest('[data-debug-badge]')) return
        if (el.getAttribute('aria-hidden') === 'true') return
        // skip empty spans
        if (el.tagName === 'SPAN' && !el.textContent?.trim() && !el.querySelector('img,svg')) return
        n += 1
        el.setAttribute('data-debug-num', String(n))
        el.style.outline = '1px dashed rgba(118, 150, 173, 0.9)'
        el.style.outlineOffset = '2px'

        const num = document.createElement('span')
        num.setAttribute('data-debug-badge', '1')
        num.textContent = String(n)
        Object.assign(num.style, {
          position: 'absolute',
          top: '0',
          left: '0',
          transform: 'translate(-30%, -40%)',
          zIndex: '9997',
          background: '#7696AD',
          color: '#fff',
          fontFamily: 'DM Sans, system-ui, sans-serif',
          fontSize: '10px',
          fontWeight: '700',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          pointerEvents: 'none',
          lineHeight: '1',
        })
        const pos = window.getComputedStyle(el).position
        if (pos === 'static') el.style.position = 'relative'
        el.appendChild(num)
      })
    })
  }, [on])

  useEffect(() => {
    annotate()
    if (!on) return undefined
    const t = setTimeout(annotate, 400)
    window.addEventListener('resize', annotate)
    return () => {
      clearTimeout(t)
      window.removeEventListener('resize', annotate)
    }
  }, [on, annotate])

  // re-run when route content may change
  useEffect(() => {
    if (!on) return undefined
    const mo = new MutationObserver(() => {
      clearTimeout(mo._t)
      mo._t = setTimeout(annotate, 300)
    })
    mo.observe(document.querySelector('main') || document.body, {
      childList: true,
      subtree: true,
    })
    return () => {
      mo.disconnect()
      clearTimeout(mo._t)
    }
  }, [on, annotate])

  return (
    <button
      type="button"
      onClick={() => setOn((v) => !v)}
      className="fixed bottom-4 left-4 z-[9999] rounded-full px-4 py-2 text-xs font-semibold tracking-wide uppercase shadow-lg border border-color-primary"
      style={{
        background: on ? '#224160' : '#EDE7DC',
        color: on ? '#EDE7DC' : '#224160',
        fontFamily: 'DM Sans, system-ui, sans-serif',
      }}
      aria-pressed={on}
      title="Toggle section/element labels"
    >
      {on ? 'Labels ON' : 'Labels OFF'}
    </button>
  )
}
